import { SequentialTimeline } from '../../libs/SequentialTimeline';
import { Config } from '../../video-data/config';
import { assertMinimum, assertNumber } from '../../validators';
import { BaseGroup } from '../BaseGroup';
import { buildChildrenForSceneUsingData } from '../../libs/buildChildrenForScene';
import type { BaseAssetSettings } from '../asset/BaseAsset';
import { BaseAsset } from '../asset/BaseAsset';
import type { LuminaryObject } from '../../types';
import { AssetSuffix, type BufferingMode, type SeekMode } from '../../enums';

/**
 */
export class Subslide extends BaseGroup implements LuminaryObject {
    override _classType = 'Subslide';
    override _name = 'subslide';

    /**
     * @internal
     */
    _timeline = new SequentialTimeline({
        name: 'subscene_timeline',
        duration: Config.MIN_SCENE_DURATION,
    });

    constructor(container: any, settings: BaseAssetSettings) {
        super(container, settings);

        this.on('assetCanPlay', () => {
            if (this.canPlay) {
                this._emitAssetsCanPlay();
            }
        });
    }

    get index(): number {
        if (!this.parent) {
            return -1;
        }
        return this.parent.children.indexOf(this as any);
    }

    _emitAssetsCanPlay() {
        if (this.slide) {
            this.slide.emit('assetsCanPlay');
        }
    }

    override _build() {
        this._buildTextInput();
    }

    async _propagateBuffering(bufferingMode: BufferingMode): Promise<void> {
        await this._bufferAtTime(this._timeline.currentTime, bufferingMode);
    }

    /**
     * Update any text asset that is being edited.
     *
     * The scope of this update includes all text assets that are considered children of this subslide
     * (i.e. will recursively search all children)
     */
    _buildTextInput() {
        this.getAssetsByType(Config.TEXT_ASSET_TYPE, { deep: true }).forEach((textAsset) => {
            textAsset.updateTextInput();
        });
    }

    override _recursiveSeek(_progress: number, seekMode: SeekMode) {
        // override our default implementation so we can remap our slide progress to a local sub-slide progress. Depending
        // on the slide's progress, a given subslide will be inactive and another will get it's progress mapped to its
        // local progress.
        this.visible = this._timeline.active;
        if (!this._timeline.active) {
            return;
        }
        super._recursiveSeek(this._timeline.progress, seekMode);
    }

    /**
     * Returns true if all assets (deeply) within this object have finished loading, or have
     * failed to load. If any assets are still loading or retrying a load this will be false.
     */
    get areAssetsDoneTryingToLoad(): boolean {
        return this._childAssets.every((asset) => asset.isDoneTryingToLoad);
    }

    /**
     * Returns true if all assets (deeply) within this object have successfully loaded.
     */
    override get isLoaded(): boolean {
        return this._childAssets.every((asset) => asset.isLoaded);
    }

    /**
     * Determines whether this component is active at the currentTime. Active means that the component will be
     * evaluated and visible when rendering.
     */
    get active(): boolean {
        return this._timeline.active;
    }

    get currentTime(): number {
        return this._timeline.currentTime;
    }

    /**
     * Get the subslide's end time relative to luminary.
     * @returns the end time of the subslide
     */
    get endTime(): number {
        return this.startTime + this.duration;
    }

    /**
     * Returns the subslide's start time relative to luminary.
     * @returns the start time of the subslide
     */
    get startTime(): number {
        return this._timeline.startTimeRelativeToAncestor(this.root._timeline);
    }

    /**
     * The start time relative to the scene containing this subscene.
     */
    get relativeStartTime(): number {
        return this._timeline._relativeStartTime;
    }

    get animationManagers() {
        return this.getAssets().map((child) => child.animationManager);
    }

    /**
     * Determine whether or not the component is animated
     */
    get isAnimated(): boolean {
        // since we currently always create both text/image elements, but don't necessarily set them both, this value
        // can return a false negative. With the new update flow we'll be able to change this behavior is currently a
        // result of our rigid video data schema.
        return this.animationManagers.every((manager) => !manager.isEmpty());
    }

    /**
     * @internal
     */
    override set duration(value: number) {
        if (value === this.duration) {
            return;
        }
        assertNumber(value);
        assertMinimum(value, Config.MIN_SCENE_DURATION);
        this._timeline.duration = value;
        this.requiresRedraw = true;
    }

    override get duration() {
        return this._timeline.duration;
    }

    override get isPlaying() {
        return this._timeline.playing() && this._timeline.active;
    }

    /**
     * Generates new ids for all assets on the subslide.
     */
    generateNewAssetIds() {
        this.getAssets().forEach((child) => {
            if (child instanceof BaseAsset) {
                child.generateNewId();
            }
        });
        this.generateNewId();
    }

    override saveEditable() {
        return [
            {
                children: this.getAssets()
                    .filter((item) => item.isEditable)
                    .filter((item) => !item.hasSuffix(AssetSuffix.DisableSwap))
                    .map((item) => item.saveEditable()),
                duration: this.duration,
            },
        ];
    }

    override save(allProperties = true, clone = false) {
        const properties = super.save(allProperties, clone);
        return Object.assign(properties, this.saveChildren(allProperties, clone));
    }

    /**
     * Serializes all of the subslide's children data
     */
    saveChildren(allProperties = true, clone = false): { children: Array<object> } {
        return {
            children: this.getAssets()
                .map((item) => item.save(allProperties, clone))
                .flat(),
        };
    }

    override get readonlyProperties() {
        return ['classType', 'id'];
    }

    override get propertiesToSerialize() {
        return [];
    }

    override get editableProperties() {
        return super.editableProperties.concat(['duration']);
    }

    override load(data = {}) {
        this.clear();

        super.load(data);

        buildChildrenForSceneUsingData(this, data);

        return this;
    }

    get requiresUpdate() {
        return this.requiresRedraw;
    }

    set requiresUpdate(value: boolean) {
        // this is a no-op, but we need to implement it to satisfy the interface
    }
}
